import React from "react";

import ResponsiveChart from "components/RunPlot/ResponsiveChart";

import { ScatterChart, Scatter, Tooltip, XAxis, YAxis, ZAxis, ResponsiveContainer, Cell } from "recharts";

import styles from "./style.module.css";

const CustomToolTip = (props) => {
  const { active, payload, label } = props;
  if (!active || !payload) {
    return null;
  }

  return (
    <div className={styles.cTooltip}>
      <div className={styles.tModel}>{payload[0].payload.rule}</div>
      {payload.reverse().map((item, i) => (
        <div className={styles.tBody} key={i}>
          <div className={styles.tLabel}>
            {item.name === "score" ? "Score" : item.name === "weightedScore" ? "Weighted Score" : item.name}{" "}
          </div>
          <strong>
            {item.name === "radius"
              ? Math.abs(item.value * 100).toFixed(2) + "%"
              : item.name === "Coefficient"
                ? item.payload.coefficient
                : item.value}
          </strong>
        </div>
      ))}
    </div>
  );
};

const CustomLabel = (props) => {
  const { active, payload, label } = props;

  return (
    <text textAnchor="start" className={styles.aLabel}>
      <tspan
        x={
          props.position === "insideTopLeft"
            ? props.value
              ? 250 / props.value.length
              : 20
            : props.viewBox.x + props.viewBox.width / 2 - 33
        }
        y={props.viewBox.y + props.offset}
      >
        {props.value}
      </tspan>
    </text>
  );
};

class ClusterChart extends React.PureComponent {
  state = {
    hover: undefined
  };

  handleSelect(value) {
    const { selected, updateSelected } = this.props;

    let retSelected = selected ? { ...selected } : {};

    if (retSelected[value] === true) {
      delete retSelected[value];
    } else {
      if (retSelected[value] === undefined || retSelected[value] === false) {
        retSelected[value] = true;
      }
    }

    return updateSelected(retSelected);
  }

  renderRight() {
    const { dataset, selected, updateSelected } = this.props;
    const { hover } = this.state;

    return (
      <div className={styles.rightPanel}>
        <div className={styles.rightField} style={{ marginBottom: "15px" }} onClick={() => updateSelected({})}>
          {selected === undefined && <div className={styles.filter} />}
          {"Show All" + " (" + dataset.rulefit.length + ")"}
        </div>
        <div className={styles.rightContent}>
          {dataset &&
            dataset.fields &&
            Object.keys(dataset.fields)
              .map((f) => dataset.fields[f])
              .sort((a, b) => b.rulefit.length - a.rulefit.length)
              .map((f) => (
                <div
                  key={f.label}
                  className={styles.rightField}
                  onMouseEnter={() => this.setState({ hover: f.label })}
                  onMouseLeave={() => this.setState({ hover: undefined })}
                  onClick={() => this.handleSelect(f.label)}
                >
                  {selected && selected[f.label] === true && <div className={styles.filter} />}
                  {f.label + " (" + f.rulefit.length + ")"}
                </div>
              ))}
        </div>
      </div>
    );
  }

  render() {
    const { id, fill, cData, dataset, selected } = this.props;
    const { hover } = this.state;

    if (dataset === undefined) {
      return <div className={styles.emptyMsg}>{"Dataset not selected"}</div>;
    } else if (cData.length === 0) {
      <div className={styles.emptyMsg}>{"Cluster not available"}</div>;
    }

    const data = cData
      .filter((d) => {
        if (selected) {
          let show = 0;
          const sSelected = Object.keys(selected).filter((s) => selected[s] !== false);

          sSelected.forEach((s) => {
            if (d.rule.indexOf(s) !== -1) {
              show++;
            }
          });

          return show === sSelected.length;
        } else {
          return true;
        }
      })
      .map((d, i) => ({
        ...d,
        x:
          d.coefficient > 0
            ? i % 2 === 0
              ? -Math.sqrt(d.coefficient)
              : Math.sqrt(d.coefficient)
            : i % 2 === 0
              ? -Math.sqrt(Math.abs(d.coefficient)) * 2
              : Math.sqrt(Math.abs(d.coefficient)) * 2,
        y: d.coefficient > 0 ? 1 - d.support : -(1 - d.support),
        z: d.support
      }));

    const isContinuous = false;

    const yDataMin = Math.min.apply(
      Math,
      data.map((m) => m.y)
    );
    const yDataMax = Math.max.apply(
      Math,
      data.map((m) => m.y)
    );
    const yDelta = yDataMax - yDataMin;

    const xDataMin = Math.min.apply(
      Math,
      data.map((m) => m.x)
    );
    const xDataMax = Math.max.apply(
      Math,
      data.map((m) => m.x)
    );
    const xDelta = xDataMax - xDataMin;

    const xOffset = isContinuous ? (xDelta > yDelta ? 0 : (yDelta - xDelta) / 2) : 0;
    const yOffset = isContinuous ? (yDelta > xDelta ? 0 : (xDelta - yDelta) / 2) : 0;

    let xDomain = [xDataMin - Math.abs(xDelta) * 0.1 - xOffset, xDataMax + Math.abs(xDelta) * 0.1 + xOffset];

    const yDomain = [yDataMin - Math.abs(yDelta) * 0.1 - yOffset, yDataMax + Math.abs(yDelta) * 0.1 + yOffset];

    return (
      <div className={fill ? styles.fillRunPlot : styles.runPlot} id="runPlot">
        {selected && Object.keys(selected).length > 0 && (
          <div className={styles.selected}>
            {"Filter: " +
              Object.keys(selected)
                .map((s) => ({ label: s, val: selected[s] }))
                .filter((s) => s.val === true)
                .map((s, i) => " " + s.label)}
          </div>
        )}
        <div className={styles.cChart}>
          <ResponsiveContainer>
            <ScatterChart margin={{ top: 50, right: 50, bottom: 50, left: 50 }}>
              <XAxis type="number" dataKey="x" name={"Coefficient"} domain={xDomain} hide />
              <YAxis type="number" dataKey="y" name={"Support"} domain={yDomain} hide />
              <ZAxis dataKey="z" type="number" range={[100, 8000]} name="radius" />
              <Scatter name="rule" data={data} fill="#ff7300">
                {data.map((entry, index) => {
                  const xD = entry.x / 0.2;
                  const yD = entry.y / yDomain[1];

                  const fill = entry.coefficient > 0 ? [81, 47, 119] : [14, 168, 169];

                  let shouldHover = entry.rule.indexOf(hover) !== -1;

                  if (selected && Object.keys(selected).filter((s) => s === hover).length > 0) {
                    shouldHover = false;
                  }

                  return (
                    <Cell
                      key={`cell-${index}`}
                      fill={
                        entry.rule && hover && shouldHover
                          ? "#F47C20"
                          : "rgba(" +
                            fill[0] +
                            ", " +
                            fill[1] +
                            ", " +
                            fill[2] +
                            ", " +
                            Math.abs(entry.coefficient * 10) +
                            ")"
                      }
                      stroke={entry.coefficient > 0 ? "var(--aible-color)" : "rgb(14, 168, 189)"}
                      strokeWidth={1}
                      style={{ radius: "5px", transition: "all 0.2s" }}
                    />
                  );
                })}
              </Scatter>
              <Tooltip cursor={{ strokeDasharray: "3 3" }} content={<CustomToolTip />} />
            </ScatterChart>
          </ResponsiveContainer>
        </div>
        {this.renderRight()}
      </div>
    );
  }
}

function shuffle(array) {
  var currentIndex = array.length,
    temporaryValue,
    randomIndex;

  // While there remain elements to shuffle...
  while (0 !== currentIndex) {
    // Pick a remaining element...
    randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex -= 1;

    // And swap it with the current element.
    temporaryValue = array[currentIndex];
    array[currentIndex] = array[randomIndex];
    array[randomIndex] = temporaryValue;
  }

  return array;
}

export default ResponsiveChart(ClusterChart);
